/**
 * DashboardViewPresets.jsx — Phase 144 — Home Overview's "View 1 / View 2 /
 * View 3" fixed preset slots (distinct from the free-naming SavedViewsRow
 * used elsewhere). Clicking a View N link applies whatever filter combo is
 * saved in that slot (or clears filters if the slot is still empty) and
 * marks it the "active" slot; "Pin Current View" overwrites the active
 * slot with the dashboard's live filters. Backed by
 * GET/PUT /users/me/dashboard-view-presets*.
 *
 * Phase 145 — each slot can be renamed to something memorable ("My Sprint",
 * "BU2 Overview") by double-clicking its label — GET/PUT
 * /users/me/dashboard-view-preset-names.
 */
import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Pin } from "lucide-react";

const DEFAULT_PRESETS = { "1": null, "2": null, "3": null, active: 1 };
const DEFAULT_NAMES = { "1": "View 1", "2": "View 2", "3": "View 3" };

export default function DashboardViewPresets({ currentFilters, onApply }) {
  const [presets, setPresets] = useState(DEFAULT_PRESETS);
  const [names, setNames] = useState(DEFAULT_NAMES);
  const [pinning, setPinning] = useState(false);
  const [renaming, setRenaming] = useState(null); // index currently being edited
  const [draftName, setDraftName] = useState("");

  useEffect(() => {
    api.get("/users/me/dashboard-view-presets").then(({ data }) => setPresets(data || DEFAULT_PRESETS)).catch(() => {});
    api.get("/users/me/dashboard-view-preset-names").then(({ data }) => setNames(data || DEFAULT_NAMES)).catch(() => {});
  }, []);

  const switchTo = async (index) => {
    try {
      const { data } = await api.put("/users/me/dashboard-view-presets/switch", { index });
      setPresets(data);
      const slot = data[String(index)];
      onApply(slot || { person_id: null, project_id: null, business_unit: null, status_filter: null });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const pinCurrent = async () => {
    setPinning(true);
    try {
      const { data } = await api.put("/users/me/dashboard-view-presets/pin", currentFilters);
      setPresets(data);
      toast.success(`Pinned to ${names[String(presets.active)] || `View ${presets.active}`}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPinning(false); }
  };

  const startRename = (n) => { setRenaming(n); setDraftName(names[String(n)] || `View ${n}`); };

  const saveRename = async () => {
    const index = renaming;
    setRenaming(null);
    try {
      const { data } = await api.put("/users/me/dashboard-view-preset-names", { index, name: draftName });
      setNames(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="flex items-center gap-2 shrink-0" data-testid="dashboard-view-presets">
      {[1, 2, 3].map((n) => {
        const filled = !!presets[String(n)];
        const isActive = presets.active === n;
        if (renaming === n) {
          return (
            <input
              key={n}
              autoFocus
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              onBlur={saveRename}
              onKeyDown={(e) => { if (e.key === "Enter") saveRename(); if (e.key === "Escape") setRenaming(null); }}
              maxLength={40}
              className="h-6 w-[110px] text-[11px] font-semibold rounded-full px-2.5 border border-[#F54800] outline-none"
              data-testid={`dashboard-view-preset-rename-input-${n}`}
            />
          );
        }
        return (
          <button
            key={n}
            type="button"
            onClick={() => switchTo(n)}
            onDoubleClick={(e) => { e.stopPropagation(); startRename(n); }}
            className={`px-2.5 py-1 text-[11px] font-semibold rounded-full transition-colors max-w-[130px] truncate ${
              isActive ? "bg-[#F54800] text-white" : filled ? "bg-slate-100 text-slate-700 hover:bg-slate-200" : "text-slate-400 border border-dashed border-slate-300 hover:border-slate-400"
            }`}
            data-testid={`dashboard-view-preset-${n}`}
            title={`${filled ? "Apply" : "Empty —"} ${names[String(n)] || `View ${n}`} (double-click to rename)`}
          >
            {names[String(n)] || `View ${n}`}
          </button>
        );
      })}
      <button
        type="button"
        onClick={pinCurrent}
        disabled={pinning}
        className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold rounded-full text-white bg-slate-900 hover:bg-slate-800 disabled:opacity-60"
        data-testid="dashboard-pin-current-view"
        title={`Save current filters to ${names[String(presets.active)] || `View ${presets.active}`}`}
      >
        <Pin size={10} /> Pin Current View
      </button>
    </div>
  );
}
